import { forwardRef, useRef, useImperativeHandle, useState, useEffect } from 'react';
import Image from 'next/image';

//* Style
import ImageStyle from './style';

const CustomImage = forwardRef(({ src = '/images/default.jpeg', alt = 'image', objectFit = 'cover', objectPosition = 'center', priority = false, className, onLoad, blurDataURL, sizes, width, height, quality, rootMargin = '250px', ...rest }, ref) => {
	//! Ref
	const imgRef = useRef();

	//! State
	const [loaded, setLoaded] = useState(false);
	const [shouldLoad, setShouldLoad] = useState(Boolean(priority));
	const [autoSizes, setAutoSizes] = useState('100vw');

	useImperativeHandle(ref, () => imgRef.current, []);

	//! Setup scroll-based loading
	useEffect(() => {
		if (priority || shouldLoad) {
			return;
		}

		const node = imgRef.current;

		if (!node) {
			return;
		}

		const isMapboxMarker = Boolean(node.closest('.mapboxgl-marker'));

		if (isMapboxMarker || typeof IntersectionObserver === 'undefined') {
			setShouldLoad(true);
			return;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setShouldLoad(true);
					observer.disconnect();
				}
			},
			{ rootMargin }
		);

		observer.observe(node);

		return () => observer.disconnect();
	}, [priority, shouldLoad, rootMargin]);

	//! Handle Image Load
	const onLoadCallback = (event) => {
		setLoaded(true);
		typeof onLoad === 'function' && onLoad(true, event);
	};

	const isSvg = typeof src === 'string' && src.endsWith('.svg');
	const blurEnabled = Boolean(blurDataURL && !isSvg);
	const usesFillLayout = !(typeof width === 'number' && typeof height === 'number');
	const imageDimensions = usesFillLayout ? { fill: true } : { width, height };

	useEffect(() => {
		if (sizes || !usesFillLayout || typeof window === 'undefined') {
			return;
		}

		const node = imgRef.current;

		if (!node) {
			return;
		}

		const updateAutoSizes = () => {
			if (!imgRef.current) {
				return;
			}

			const rect = imgRef.current.getBoundingClientRect();
			const viewportWidth = window.innerWidth || rect.width;

			if (!rect.width || !viewportWidth) {
				return;
			}

			const percentage = Math.min(100, Math.max(1, (rect.width / viewportWidth) * 100));
			const computedSizes = `${percentage.toFixed(2)}vw`;

			setAutoSizes((prev) => (prev === computedSizes ? prev : computedSizes));
		};

		updateAutoSizes();

		let resizeObserver;

		if (typeof ResizeObserver !== 'undefined') {
			resizeObserver = new ResizeObserver(updateAutoSizes);
			resizeObserver.observe(node);
		}

		window.addEventListener('resize', updateAutoSizes);

		return () => {
			resizeObserver?.disconnect();
			window.removeEventListener('resize', updateAutoSizes);
		};
	}, [sizes, usesFillLayout]);

	return (
		<ImageStyle
			ref={imgRef}
			className={`imageCont ${className || ''}`}
			style={{ backgroundColor: loaded ? 'transparent' : '#ECEDEE' }}
		>
			<span>
				{shouldLoad ? (
					<Image
						{...imageDimensions}
						draggable={false}
						placeholder={blurEnabled ? 'blur' : undefined}
						priority={priority}
						alt={alt}
						sizes={usesFillLayout ? sizes || autoSizes : undefined}
						onLoad={onLoadCallback}
						src={src}
						loading={priority ? 'eager' : 'lazy'}
						unoptimized={isSvg}
						blurDataURL={blurEnabled ? blurDataURL : undefined}
						style={{ opacity: loaded ? 1 : 0, objectFit, objectPosition }}
						quality={quality}
						{...rest}
					/>
				) : (
					<div className='image-placeholder' />
				)}
			</span>
		</ImageStyle>
	);
});

CustomImage.displayName = 'CustomImage';

export default CustomImage;
